import { PlusOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import { EditPictureModal } from 'app/components/commons/EditPictureModal/EditPictureModal';
import { PictureList } from 'app/components/commons/ImageUploader/PictureList/PictureList';
import { GalleryModal } from 'app/components/pages/Galleries/GalleryModal/GalleryModal';
import {
  useEditPicture,
  useRemoveHotelPicture,
} from 'app/hooks/data/pictures/useSavePicture';
import type { Picture } from 'app/typings';

import './Pictures.scss';

const DEFAULT_PICTURES_CATEGORIES = [
  { name: 'Media gallery', type: 'official' },
];
const DEFAULT_MODAL_SUBTITLE =
  'Select photos in your media gallery or upload new ones to illustrate this space';

export const PictureGallery: FC<Props> = ({
  value,
  onChange,
  sectionTitle,
  hotelId,
  picturesCategories = DEFAULT_PICTURES_CATEGORIES,
  kind = 'official',
  previewable = false,
  removable = false,
  editable = false,
  draggable = false,
  modalSubtitle = DEFAULT_MODAL_SUBTITLE,
  fetchRooms = false,
  maxPictures,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [pictureToEdit, setPictureToEdit] = useState<Picture | null>(null);
  const pictures = internalValue ?? [];

  const editPictureMutation = useEditPicture();
  const removePictureMutation = useRemoveHotelPicture();

  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  if (!onChange) {
    return null;
  }

  const handleOnChange = (newValue: Picture[]) => {
    setInternalValue(newValue);
    onChange(newValue);
  };

  const addPictures = (newPictures: Picture[]) => {
    if (!maxPictures || pictures.length + newPictures.length < maxPictures) {
      handleOnChange([...pictures, ...newPictures]);
    } else {
      handleOnChange([
        ...pictures.slice(0, pictures.length - newPictures.length),
        ...newPictures,
      ]);
    }
  };

  const handleRemovePicture = (index: number) => {
    if (index >= 0) {
      handleOnChange([
        ...pictures.slice(0, index),
        ...pictures.slice(index + 1),
      ]);
    }
  };

  const onRemove = (uid: string) => {
    const index = pictures.findIndex(
      (pic) => uid === `${pic.pictureId}-${pic.id}`
    );

    handleRemovePicture(index);
  };

  const updatePicture = (values: Partial<Picture>) => {
    const newPicture = { ...pictureToEdit, ...values } as Picture;

    editPictureMutation.mutate(newPicture);

    const pictureIdx = pictures.findIndex((pic) => pic.id === newPicture.id);

    pictures[pictureIdx] = newPicture;
    handleOnChange([...pictures]);
  };

  const handleDeletePicture = () => {
    if (pictureToEdit && hotelId) {
      removePictureMutation.mutate({
        hotelId,
        pictureId: pictureToEdit.id,
      });
    }

    if (pictureToEdit) {
      onRemove(`${pictureToEdit.pictureId}-${pictureToEdit.id}`);
      setPictureToEdit(null);
    }
  };

  return (
    <div className="pictures-content">
      <PictureList
        pictures={pictures || []}
        uploadingItems={{}}
        onChange={handleOnChange}
        remove={removable ? onRemove : undefined}
        edit={editable ? setPictureToEdit : undefined}
        customButton={
          <div onClick={() => setModalIsVisible(true)}>
            <PlusOutlined />
            <span className="text">Add</span>
          </div>
        }
        previewable={previewable}
        draggable={draggable}
      />
      <GalleryModal
        visible={modalIsVisible}
        title={sectionTitle}
        onOk={(newPictures) => {
          addPictures(newPictures as unknown as Picture[]);
          setModalIsVisible(false);
        }}
        onCancel={() => setModalIsVisible(false)}
        hotelId={hotelId}
        okText="Save"
        cancelText="Cancel"
        categories={picturesCategories}
        subTitle={modalSubtitle}
        kind={kind}
        fetchRooms={fetchRooms}
        maxPictures={maxPictures}
      />
      {!!pictureToEdit && (
        <EditPictureModal
          picture={pictureToEdit}
          closeModal={() => setPictureToEdit(null)}
          savePicture={(newPictureValues) => {
            updatePicture(newPictureValues);
            setPictureToEdit(null);
          }}
          deletePicture={handleDeletePicture}
          kind={kind}
          removable={removable}
        />
      )}
    </div>
  );
};

type Props = {
  value?: Picture[];
  onChange?: (pictures: Picture[]) => void;
  sectionTitle: string;
  hotelId?: number;
  picturesCategories?: { name: string; type: string }[];
  kind?: string;
  previewable?: boolean;
  removable?: boolean;
  editable?: boolean;
  draggable?: boolean;
  modalSubtitle?: string;
  fetchRooms?: boolean;
  maxPictures?: number;
};
